import { DvaReducer } from '@/models/shared';
import { AnyAction } from 'redux';
import { EffectsCommandMap, Subscription } from 'dva';
import { PolicyFullInfo_Type } from '@/type/contractTypes';
import {
  ConnectState,
  ResourceVersionEditorPageModelState,
} from '@/models/connect';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import { history } from 'umi';
import moment from 'moment';
import fMessage from '@/components/fMessage';
import { handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2 } from '@/utils/service';
import {
  ICollectionCreateVersionDraftType,
  IResourceCreateVersionDraftType,
} from '@/type/resourceTypes';

export interface CollectionManagerState {
  sider_state: 'loading' | 'loaded';
  sider_resourceID: string;
  sider_resourceName: string;
  sider_resourceTitle: string;
  sider_resourceState: '' | 'unreleased' | 'offline' | 'online';
  sider_resourceCover: string;
  sider_resourceType: string[];
  sider_resourceVersions: string[];
  sider_isChoice: boolean;
  sider_showPage:
    | ''
    | 'info'
    | 'policy'
    | 'contract'
    | 'dependency'
    | 'versionInfo';
  sider_hasAuthProblem: boolean;
  sider_policies: PolicyFullInfo_Type[];

  version_pageState: 'loading' | 'loaded';
  version_resourceID: string;
  version_version: string;
  version_versions: string[];
  version_resourceInfo: {
    resourceID: string;
    resourceName: string;
    resourceType: string[];
    isFinish: boolean;
    isAutomatic: boolean;
    itemsTotalCount: number;
    updateDate: string;
  } | null;
  version_resourceTypeConfig: {
    isSupportDownload: boolean;
    isSupportOptionalConfig: boolean;
  };
  version_collectionItemsSetting: {
    collection_sort_list:
      | 'collection_sort_ascending'
      | 'collection_sort_descending';
    collection_item_title:
      | 'collection_item_title_rtitle'
      | 'collection_item_title_sn'
      | 'collection_item_title_empty'
      | 'collection_item_title_custom';
    collection_item_no_display:
      | 'collection_item_no_display_show'
      | 'collection_item_no_display_hide';
    collection_item_image_display:
      | 'collection_item_image_display_show'
      | 'collection_item_image_display_hide';
    collection_item_descr_display:
      | 'collection_item_descr_display_show'
      | 'collection_item_descr_display_hide';
    collection_view: 'collection_view_list' | 'collection_view_card';
  };
  version_resourceKeywords: string;
  version_collectionItems: {
    itemID: string;
    itemTitle: string;
    itemSerial: number;
    itemNumber: string;
    resourceInfo: {
      id: string;
      cover: string;
      name: string;
      title: string;
      version: string;
      policy: string[];
      type: string[];
      status: 0 | 1 | 2 | 4;
      authProblem: boolean;
      updateDate: string;
      username: string;
      useAvatar: string;
      isChoice: boolean;
      intro: string;
      releaseDateTime?: string;
    };
  }[];
  version_resources_state: 'loading' | 'noData' | 'noSearchResult' | 'loaded';
  version_resources_totalCount: number;
  version_resources_pageCurrent: number;
  version_resources_pageSize: number;
  version_descriptionText: string;
  version_graphShow: boolean;
  // version_rawProperties: {
  //   key: string;
  //   name: string;
  //   value: string;
  //   description: string;
  // }[];
  // version_additionalProperties: {
  //   key: string;
  //   name: string;
  //   value: string;
  //   description: string;
  // }[];
  version_systemProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
    type: 'raw' | 'additional';
    valueConfig?: {
      text?: {
        nullable: boolean;
        minLength: number;
        maxLength: number;
      };
      textArea?: {
        nullable: boolean;
        minLength: number;
        maxLength: number;
      };
      integer?: {
        nullable: boolean;
        min: number;
        max: number;
      };
      decimal?: {
        nullable: boolean;
        minDecimal: number;
        maxDecimal: number;
        precision: number;
      };
      date?: {
        nullable: boolean;
        startDate: string;
        limitDate: string;
      };
      dataTime?: {
        nullable: boolean;
        startDateTime: string;
        limitDateTime: string;
      };
      configText?: {
        nullable: boolean;
        defaultValue: string;
        minLength: number;
        maxLength: number;
      };
      configEnum?: {
        nullable: boolean;
        defaultValue: string;
        options: string[];
      };
    };
  }[];
  version_propertyKeysSort: string[];
  version_customProperties: {
    key: string;
    name: string;
    value: string;
    description: string;
  }[];
  version_customConfigurations: {
    key: string;
    name: string;
    description: string;
    type: 'input' | 'select';
    input: string;
    select: string[];
  }[];
  version_baseUpcastResources: {
    resourceID: string;
    resourceName: string;
  }[];
  version_directDependencies: {
    id: string;
    name: string;
    type: 'resource' | 'object';
    versionRange?: string;
  }[];
  // version_resolveResources: {
  //   resourceId: string;
  //   contracts: {
  //     policyId: string;
  //   }[];
  // }[];
  version_authExcludedItems: {
    resourceID: string;
    resourceName: string;
    contracts: {
      policyID: string;
      contractID: string;
    }[];
  }[];
  version_reload: number;
  version_draftSaving: boolean;
  version_draftSaveTime: string;
  version_collectionItemsChanged: boolean;
  version_otherChanged: boolean;

  info_pageState: 'loading' | 'loaded';
  info_resourceID: string;
  info_resourceInfo: {
    coverImages: string[];
    resourceName: string;
    resourceTitle: string;
    resourceTypeCode: string;
    resourceType: string[];
    intro: string;
    tags: string[];
  } | null;

  info_title_IsEditing: boolean;
  info_title_Input: string;
  info_title_Error: string;

  info_introduction_EditorText: string;

  info_username: string;
  info_updateSetting: {
    isFinish: boolean;
    automatic: boolean;
    conditionType: 'every' | 'some';
    conditions: {
      key: 'resourceTitle' | 'resourceTypeCode' | 'authIdentity';
      limitOperatorType:
        | 'INCLUDES'
        | 'NOT_INCLUDES'
        | 'STARTS_WITH'
        | 'ENDS_WITH'
        | 'EQUAL'
        | 'NOT_EQUAL';
      value: string;
      valueError: string;
    }[];
  };

  info_hasPermission: boolean;
}

export interface ChangeAction extends AnyAction {
  type: 'change' | 'collectionManager/change';
  payload: Partial<CollectionManagerState>;
}

export interface OnMount_Page_Action extends AnyAction {
  type: 'collectionManager/onMount_Page';
}

export interface OnUnmount_Page_Action extends AnyAction {
  type: 'collectionManager/onUnmount_Page';
}

export interface Sider_OnMount_Page_Action extends AnyAction {
  type: 'collectionManager/sider_onMount_Page';
  payload: {
    resourceID: string;
  };
}

export interface Sider_OnUnmount_Page_Action extends AnyAction {
  type: 'collectionManager/sider_onUnmount_Page';
}

export interface Sider_OnChange_Page_Action extends AnyAction {
  type: 'collectionManager/sider_onChange_Page';
  payload: {
    page: CollectionManagerState['sider_showPage'];
  };
}

export interface Sider_OnUpdate_Data_Action extends AnyAction {
  type: 'collectionManager/sider_onUpdate_Data';
}

export interface Sider_FetchInfoAction extends AnyAction {
  type: 'sider_fetchInfo';
}

export interface Version_OnMount_Page_Action extends AnyAction {
  type: 'collectionManager/version_onMount_Page';
  payload: {
    resourceID: string;
    version: string;
  };
}

export interface Version_OnUnmount_Page_Action extends AnyAction {
  type: 'collectionManager/version_onUnmount_Page';
}

export interface Version_FetchDataSourceAction extends AnyAction {
  type: 'collectionManager/version_fetchDataSource' | 'version_fetchDataSource';
}

export interface Version_Fetch_CollectionItems_Action extends AnyAction {
  type:
    | 'collectionManager/version_Fetch_CollectionItems'
    | 'version_Fetch_CollectionItems';
}

export interface Version_SyncAllPropertiesAction extends AnyAction {
  type:
    | 'version_syncAllProperties'
    | 'collectionManager/version_syncAllProperties';
}

export interface Version_SaveDraft_Action extends AnyAction {
  type: 'version_SaveDraft' | 'collectionManager/version_SaveDraft';
}

export interface Version_SaveDate_Action extends AnyAction {
  type: 'version_SaveDate' | 'collectionManager/version_SaveDate';
}

export interface Info_OnMount_Page_Action extends AnyAction {
  type: 'collectionManager/info_onMount_Page';
  payload: {
    resourceID: string;
  };
}

export interface Info_OnUnmount_Page_Action extends AnyAction {
  type: 'collectionManager/info_onUnmount_Page';
}

export interface Info_OnClick_EditTitleBtn_Action extends AnyAction {
  type: 'collectionManager/info_onClick_EditTitleBtn';
}

export interface Info_OnClick_CancelEditTitleBtn_Action extends AnyAction {
  type: 'collectionManager/info_onClick_CancelEditTitleBtn';
}

export interface Info_OnClick_SaveTitleBtn_Action extends AnyAction {
  type: 'collectionManager/info_onClick_SaveEditTitleBtn';
  payload: {
    trigger: 'resource' | 'collection';
  };
}

export interface Info_OnChange_TitleInput_Action extends AnyAction {
  type: 'collectionManager/info_onChange_TitleInput';
  payload: {
    value: string;
  };
}

export interface Info_OnClick_SaveIntroductionBtn_Action extends AnyAction {
  type: 'collectionManager/info_onClick_SaveIntroductionBtn';
}

export interface Info_OnChange_Cover_Action extends AnyAction {
  type: 'collectionManager/info_onChange_Cover';
  payload: {
    value: string;
    trigger: 'resource' | 'collection';
  };
}

export interface Info_OnChange_Labels_Action extends AnyAction {
  type: 'collectionManager/info_onChange_Labels';
  payload: {
    value: string[];
  };
}

export interface Info_OnChangeEditorAction extends AnyAction {
  type: 'collectionManager/info_onChangeEditor';
  payload: string;
}

export interface Info_Post_UpdateSetting_Action extends AnyAction {
  type: 'info_Post_UpdateSetting' | 'collectionManager/info_Post_UpdateSetting';
}

interface CollectionManagerType {
  namespace: 'collectionManager';
  state: CollectionManagerState;
  effects: {
    onMount_Page: (
      action: OnMount_Page_Action,
      effects: EffectsCommandMap,
    ) => void;
    onUnmount_Page: (
      action: OnUnmount_Page_Action,
      effects: EffectsCommandMap,
    ) => void;

    sider_onMount_Page: (
      action: Sider_OnMount_Page_Action,
      effects: EffectsCommandMap,
    ) => void;
    sider_onUnmount_Page: (
      action: Sider_OnUnmount_Page_Action,
      effects: EffectsCommandMap,
    ) => void;
    sider_onChange_Page: (
      action: Sider_OnChange_Page_Action,
      effects: EffectsCommandMap,
    ) => void;
    sider_onUpdate_Data: (
      action: Sider_OnUpdate_Data_Action,
      effects: EffectsCommandMap,
    ) => void;
    sider_fetchInfo: (
      action: Sider_FetchInfoAction,
      effects: EffectsCommandMap,
    ) => void;

    version_onMount_Page: (
      action: Version_OnMount_Page_Action,
      effects: EffectsCommandMap,
    ) => void;
    version_onUnmount_Page: (
      action: Version_OnUnmount_Page_Action,
      effects: EffectsCommandMap,
    ) => void;
    version_fetchDataSource: (
      action: Version_FetchDataSourceAction,
      effects: EffectsCommandMap,
    ) => void;
    version_Fetch_CollectionItems: (
      action: Version_Fetch_CollectionItems_Action,
      effects: EffectsCommandMap,
    ) => void;
    version_syncAllProperties: (
      action: Version_SyncAllPropertiesAction,
      effects: EffectsCommandMap,
    ) => void;
    version_SaveDraft: (
      action: Version_SaveDraft_Action,
      effects: EffectsCommandMap,
    ) => void;
    version_SaveDate: (
      action: Version_SaveDate_Action,
      effects: EffectsCommandMap,
    ) => void;

    info_onMount_Page: (
      action: Info_OnMount_Page_Action,
      effects: EffectsCommandMap,
    ) => void;
    info_onUnmount_Page: (
      action: Info_OnUnmount_Page_Action,
      effects: EffectsCommandMap,
    ) => void;

    info_onClick_EditTitleBtn: (
      action: Info_OnClick_EditTitleBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
    info_onClick_CancelEditTitleBtn: (
      action: Info_OnClick_CancelEditTitleBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
    info_onClick_SaveEditTitleBtn: (
      action: Info_OnClick_SaveTitleBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
    info_onChange_TitleInput: (
      action: Info_OnChange_TitleInput_Action,
      effects: EffectsCommandMap,
    ) => void;
    info_onClick_SaveIntroductionBtn: (
      action: Info_OnClick_SaveIntroductionBtn_Action,
      effects: EffectsCommandMap,
    ) => void;
    info_onChange_Cover: (
      action: Info_OnChange_Cover_Action,
      effects: EffectsCommandMap,
    ) => void;
    info_onChange_Labels: (
      action: Info_OnChange_Labels_Action,
      effects: EffectsCommandMap,
    ) => void;
    info_Post_UpdateSetting: (
      action: Info_Post_UpdateSetting_Action,
      effects: EffectsCommandMap,
    ) => void;
  };
  reducers: {
    change: DvaReducer<CollectionManagerType, ChangeAction>;
  };
  subscriptions: {
    setup: Subscription;
  };
}

const initStates: CollectionManagerState = {
  sider_state: 'loading',
  sider_resourceID: '',
  sider_resourceName: '',
  sider_resourceTitle: '',
  sider_resourceState: '',
  sider_resourceCover: '',
  sider_resourceType: [],
  sider_resourceVersions: [],
  sider_isChoice: false,
  sider_showPage: '',
  sider_hasAuthProblem: false,
  sider_policies: [],

  version_pageState: 'loading',
  version_resourceID: '',
  version_version: '',
  version_versions: [],
  version_resourceInfo: null,
  version_resourceTypeConfig: {
    isSupportDownload: false,
    isSupportOptionalConfig: false,
  },
  version_collectionItemsSetting: {
    // collection_sorting: 'collection_sort_newest',
    collection_sort_list: 'collection_sort_ascending',
    collection_item_title: 'collection_item_title_rtitle',
    collection_item_no_display: 'collection_item_no_display_show',
    collection_item_image_display: 'collection_item_image_display_show',
    collection_item_descr_display: 'collection_item_descr_display_show',
    collection_view: 'collection_view_list',
  },
  version_resourceKeywords: '',
  version_collectionItems: [],
  version_resources_state: 'loading',
  version_resources_totalCount: 0,
  version_resources_pageCurrent: 1,
  version_resources_pageSize: 10,
  // version_descriptionFullScreen: false,
  version_descriptionText: '',
  version_graphShow: true,
  // version_rawProperties: [],
  // version_additionalProperties: [],
  version_systemProperties: [],
  version_propertyKeysSort: [],
  version_customProperties: [],

  version_customConfigurations: [],
  version_directDependencies: [],
  version_baseUpcastResources: [],
  // version_resolveResources: [],
  version_authExcludedItems: [],
  version_reload: 0,
  version_draftSaving: false,
  version_draftSaveTime: '',
  version_collectionItemsChanged: false,
  version_otherChanged: false,

  info_pageState: 'loading',
  info_resourceID: '',
  info_resourceInfo: null,

  info_title_IsEditing: false,
  info_title_Input: '',
  info_title_Error: '',

  // info_introduction_IsEditing: false,
  info_introduction_EditorText: '',
  // info_introduction_EditorText_Error: '',

  info_username: '',
  info_updateSetting: {
    isFinish: false,
    automatic: false,
    conditionType: 'every',
    conditions: [
      {
        key: 'resourceTitle',
        limitOperatorType: 'INCLUDES',
        value: '',
        valueError: '',
      },
    ],
  },

  info_hasPermission: true,
};

const Model: CollectionManagerType = {
  namespace: 'collectionManager',
  state: initStates,
  effects: {
    *onMount_Page({}: OnMount_Page_Action, {}: EffectsCommandMap) {},
    *onUnmount_Page({}: OnUnmount_Page_Action, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: initStates,
      });
    },
    *sider_onMount_Page(
      { payload }: Sider_OnMount_Page_Action,
      { select, put }: EffectsCommandMap,
    ) {
      const { collectionManager }: ConnectState = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );
      // console.log(payload, 'payloadiosdjlkfjlsdkjflkj lkjsdl;kfjlksdj');
      if (payload.resourceID === collectionManager.sider_resourceID) {
        return;
      }
      // console.log(resourceSider.resourceID, 'resourceSider.resourceIDsdiofjlskdfjlkj');
      if (collectionManager.sider_resourceID !== '') {
        return;
      }
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          sider_resourceID: payload.resourceID,
        },
      });
      yield put<Sider_FetchInfoAction>({
        type: 'sider_fetchInfo',
      });
    },
    *sider_onUnmount_Page(
      {}: Sider_OnUnmount_Page_Action,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          sider_state: 'loading',
          sider_resourceID: '',
          sider_resourceName: '',
          sider_resourceTitle: '',
          sider_resourceState: '',
          sider_resourceCover: '',
          sider_resourceType: [],
          sider_resourceVersions: [],
          sider_isChoice: false,
          sider_showPage: '',
          sider_hasAuthProblem: false,
          sider_policies: [],
        },
      });
    },
    *sider_onChange_Page(
      { payload }: Sider_OnChange_Page_Action,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          sider_showPage: payload.page,
          // showVersionPage: payload.version,
        },
      });
    },
    *sider_onUpdate_Data(
      {}: Sider_OnUpdate_Data_Action,
      { put }: EffectsCommandMap,
    ) {
      yield put<Sider_FetchInfoAction>({
        type: 'sider_fetchInfo',
      });
    },
    *sider_fetchInfo(
      {}: Sider_FetchInfoAction,
      { call, select, put }: EffectsCommandMap,
    ) {
      const { collectionManager }: ConnectState = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );
      const params: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: collectionManager.sider_resourceID,
        isLoadPolicyInfo: 1,
        isTranslate: 1,
      };
      // console.log(params, 'params9iosdj;flkjlk lksdajf;lkjl');
      const {
        ret,
        errCode,
        msg,
        data: data_resourceInfo,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: {
          userId: number;
          status: number;
          resourceId: string;
          resourceName: string;
          resourceTitle: string;
          resourceVersions: {
            version: string;
          }[];
          latestVersion: string;
          coverImages: string[];
          resourceType: string[];
          policies: PolicyFullInfo_Type[];
          operationType: 0 | 1 | 2 | 3 | 4 | 5;
          subjectType: 1 | 4;
        };
      } = yield call(FServiceAPI.Resource.info, params);
      // console.log(data_resourceInfo, 'data_resourceInfooisdjlfkdjlfkjsdlkj');

      if (ret !== 0 || errCode !== 0) {
        fMessage(msg, 'error');
        return;
      }

      if (
        !data_resourceInfo ||
        data_resourceInfo.userId !== FUtil.Tool.getUserIDByCookies()
      ) {
        history.replace(FUtil.LinkTo.exception403());
        return;
      }

      if (data_resourceInfo.status === 2) {
        history.replace(
          FUtil.LinkTo.resourceFreeze({
            resourceID: data_resourceInfo.resourceId,
          }),
        );
        return;
      }

      if (data_resourceInfo.subjectType === 1) {
        self.location.replace(
          self.location.href.replace('/collectionSidebar/', '/sidebar/'),
        );
        return;
      }

      let authProblem: boolean = false;
      if (data_resourceInfo.latestVersion !== '') {
        const params1: Parameters<typeof FServiceAPI.Resource.batchAuth>[0] = {
          resourceIds: data_resourceInfo.resourceId,
        };
        const {
          ret,
          errCode,
          data: data_batchAuth,
        } = yield call(FServiceAPI.Resource.batchAuth, params1);

        if (ret === 0 && errCode === 0) {
          authProblem = !data_batchAuth[0].isAuth;
        } else {
          authProblem = false;
        }
      }

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          sider_state: 'loaded',
          sider_resourceName: data_resourceInfo.resourceName,
          sider_resourceTitle: data_resourceInfo.resourceTitle,
          sider_resourceState:
            data_resourceInfo.status === 0
              ? 'unreleased'
              : data_resourceInfo.status === 4
              ? 'offline'
              : 'online',
          sider_resourceCover: data_resourceInfo.coverImages[0] || '',
          sider_resourceType: data_resourceInfo.resourceType,
          sider_resourceVersions: data_resourceInfo.resourceVersions.map(
            (v) => {
              return v.version;
            },
          ),
          sider_hasAuthProblem: authProblem,
          sider_policies: data_resourceInfo.policies,
          sider_isChoice: data_resourceInfo.operationType === 5,
        },
      });
    },

    *version_onMount_Page(
      { payload }: Version_OnMount_Page_Action,
      { call, put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version_resourceID: payload.resourceID,
          version_version: payload.version,
        },
      });

      yield put<Version_FetchDataSourceAction>({
        type: 'version_fetchDataSource',
      });
      // console.log('version_Fetch_CollectionItems sdifjlksdjlkjfldkjflskj');
    },
    *version_onUnmount_Page(
      {}: Version_OnUnmount_Page_Action,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version_pageState: 'loading',
          version_resourceID: '',
          version_version: '',
          version_versions: [],
          version_resourceInfo: null,
          version_resourceTypeConfig: {
            isSupportDownload: false,
            isSupportOptionalConfig: false,
          },
          // version_resourceVersionInfo: null,
          version_collectionItemsSetting: {
            collection_sort_list: 'collection_sort_ascending',
            collection_item_title: 'collection_item_title_rtitle',
            collection_item_no_display: 'collection_item_no_display_show',
            collection_item_image_display: 'collection_item_image_display_show',
            collection_item_descr_display: 'collection_item_descr_display_show',
            collection_view: 'collection_view_list',
          },
          version_collectionItems: [],
          version_resources_totalCount: 0,
          version_resources_pageCurrent: 1,
          version_resources_pageSize: 10,
          // version_descriptionFullScreen: false,
          version_descriptionText: '',
          version_graphShow: true,
          // version_rawProperties: [],
          // version_additionalProperties: [],
          version_systemProperties: [],
          version_customProperties: [],
          version_customConfigurations: [],
          version_directDependencies: [],
          version_baseUpcastResources: [],
        },
      });
    },
    *version_fetchDataSource(
      {}: Version_FetchDataSourceAction,
      { call, put, select }: EffectsCommandMap,
    ) {
      // const params: ResourceVersionInfoParamsType1 = action.payload;
      const { collectionManager }: ConnectState = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );

      const params6: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
        resourceId: collectionManager.version_resourceID,
      };
      const {
        data: data_draft,
      }: {
        data: null | {
          resourceId: string;
          updateDate: string;
          draftData: ICollectionCreateVersionDraftType;
        };
      } = yield call(FServiceAPI.Resource.lookDraft, params6);

      const params1: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: collectionManager.version_resourceID,
        isLoadPolicyInfo: 1,
        isTranslate: 1,
      };
      // console.log(params1, 'params 手动阀手动阀撒旦;flkjlk lksdajf;lkjl');
      const {
        data: data_resourceInfo,
      }: {
        data: {
          userId: number;
          status: number;
          resourceId: string;
          resourceName: string;
          resourceVersions: {
            version: string;
          }[];
          latestVersion: string;
          coverImages: string[];
          resourceType: string[];
          resourceTypeCode: string;
          policies: PolicyFullInfo_Type[];
          baseUpcastResources: {
            resourceId: string;
            resourceName: string;
          }[];
          serializeStatus: 0 | 1; // 连载状态(0:连载中 1:已完结)
        };
      } = yield call(FServiceAPI.Resource.info, params1);

      // console.log(data_resourceInfo, 'data_resourceInfo sdifojsdolkjflsdjlkfjewiofjlskjl');

      let resourceSelectedVersion: string = collectionManager.version_version;
      if (data_resourceInfo.latestVersion === '') {
        resourceSelectedVersion = '';
      } else {
        if (resourceSelectedVersion === '') {
          resourceSelectedVersion = data_resourceInfo.latestVersion;
        }
      }

      const params4: Parameters<
        typeof FServiceAPI.Resource.getResourceTypeInfoByCode
      >[0] = {
        code: data_resourceInfo.resourceTypeCode,
      };
      const {
        data: data_ResourceTypeInfo,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: {
          resourceConfig: {
            fileCommitMode: number[];
            fileMaxSize: number;
            fileMaxSizeUnit: 1 | 2;
            supportDownload: 1 | 2;
            supportEdit: 1 | 2;
            supportOptionalConfig: 1 | 2;
          };
        };
      } = yield call(FServiceAPI.Resource.getResourceTypeInfoByCode, params4);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          // pageState: 'loaded',
          version_version: resourceSelectedVersion,
          // draft: data_draft ? data_draft.draftData : null,
          version_resourceTypeConfig: {
            // uploadEntry: uploadEntry,
            // limitFileSize: data_ResourceTypeInfo.resourceConfig.fileMaxSize * 1024 * 1024 ** data_ResourceTypeInfo.resourceConfig.fileMaxSizeUnit,
            isSupportDownload:
              data_ResourceTypeInfo.resourceConfig.supportDownload === 2,
            // isSupportEdit: data_ResourceTypeInfo.resourceConfig.supportEdit === 2,
            isSupportOptionalConfig:
              data_ResourceTypeInfo.resourceConfig.supportOptionalConfig === 2,
          },
        },
      });
      if (resourceSelectedVersion === '') {
        yield put<ChangeAction>({
          type: 'change',
          payload: {
            version_pageState: 'loaded',
            // version: resourceSelectedVersion,
          },
        });
        return;
      }

      // console.log(data_draft, 'data_draftewsdi9fojsdlikfjlsdjflksjdlkfjlskdjl');
      const params: Parameters<
        typeof FServiceAPI.Resource.resourceVersionInfo1
      >[0] = {
        resourceId: collectionManager.version_resourceID,
        // version: collectionManager.version,
        version: resourceSelectedVersion,
      };
      // console.log(params, 'params siduofjlksdjflkjsdlkfjlksdjflkjdslkfjlksjdlkfjlksj');
      const {
        data: data_versionInfo,
      }: {
        data: {
          resourceId: string;
          resourceName: string;
          resourceType: string[];
          resourceTypeCode: string;
          version: string;
          fileSha1: string;
          customPropertyDescriptors: {
            key: string;
            name: string;
            defaultValue: string;
            type:
              | 'editableText'
              | 'readonlyText'
              | 'radio'
              | 'checkbox'
              | 'select';
            candidateItems: string[];
            remark: string;
          }[];
          createDate: string;
          updateDate: string;
          description: string;
          systemPropertyDescriptors: {
            defaultValue: number | string;
            key: string;
            name: string;
            remark: string;
            valueDisplay: string;
            valueUnit: string;
            insertMode: 1 | 2;
          }[];
          dependencies: {
            resourceId: string;
            resourceName: string;
            versionRange: string;
          }[];
          catalogueProperty: {
            collection_item_count: number;
            // collection_sorting?: 'collection_sort_newest'
            //   | 'collection_sort_oldest'
            //   | 'collection_sort_title'
            //   | 'collection_sort_title_reverse'
            //   | 'collection_sort_manual'
            //   | 'collection_sort_manual_reverse',
            collection_sort_list?:
              | 'collection_sort_ascending'
              | 'collection_sort_descending';
            collection_item_title?:
              | 'collection_item_title_rtitle'
              | 'collection_item_title_sn'
              | 'collection_item_title_empty'
              | 'collection_item_title_custom';
            collection_item_no_display?:
              | 'collection_item_no_display_show'
              | 'collection_item_no_display_hide';
            collection_item_image_display?:
              | 'collection_item_image_display_show'
              | 'collection_item_image_display_hide';
            collection_item_descr_display?:
              | 'collection_item_descr_display_show'
              | 'collection_item_descr_display_hide';
            collection_view?: 'collection_view_list' | 'collection_view_card';
          };
        };
      } = yield call(FServiceAPI.Resource.resourceVersionInfo1, params);
      // console.log(data_versionInfo, 'data_versionInfo data902q3jrlkasdfasdf');
      if (!data_versionInfo) {
        history.replace(FUtil.LinkTo.exception403({}));
        return;
      }

      const params5: Parameters<
        typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2
      >[0] = {
        sha1: '',
        resourceTypeCode: data_resourceInfo.resourceTypeCode,
        inheritData: {
          additionalProperties: !!data_draft
            ? data_draft.draftData.additionalProperties.map((p) => {
                return {
                  key: p.key,
                  value: p.value,
                };
              })
            : data_versionInfo.systemPropertyDescriptors
                .filter((spd) => {
                  return spd.insertMode === 2;
                })
                .map((spd) => {
                  return {
                    key: spd.key,
                    value: spd.valueDisplay,
                  };
                }),
          customProperties: !!data_draft
            ? data_draft.draftData.customProperties
            : data_versionInfo.customPropertyDescriptors
                .filter((i) => i.type === 'readonlyText')
                .map((b) => {
                  return {
                    key: b.key,
                    name: b.name,
                    value: b.defaultValue,
                    description: b.remark,
                  };
                }),
          customConfigurations: !!data_draft
            ? data_draft.draftData.customConfigurations
            : data_versionInfo.customPropertyDescriptors
                .filter((i) => {
                  return i.type === 'editableText' || i.type === 'select';
                })
                .map((i) => ({
                  key: i.key,
                  name: i.name,
                  description: i.remark,
                  type: i.type === 'editableText' ? 'input' : 'select',
                  input: i.defaultValue,
                  select: i.candidateItems,
                })),
        },
      };
      const result: Awaited<
        ReturnType<
          typeof handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2
        >
      > = yield call(
        handleData_By_Sha1_And_ResourceTypeCode_And_InheritData2,
        params5,
      );

      const params7: Parameters<
        typeof FServiceAPI.Resource.getCollectionItems_Draft
      >[0] = {
        resourceId: collectionManager.version_resourceID,
        limit: 1,
      };

      const {
        data: data_collectionItems,
      }: {
        ret: number;
        errCode: number;
        data: {
          skip: number;
          limit: number;
          totalItem: number;
          dataList: [];
        };
      } = yield call(FServiceAPI.Resource.getCollectionItems_Draft, params7);
      // console.log(data_resourceInfo, 'data_resourceInfo wsoijflksdjflkjsklj');

      const params9: Parameters<
        typeof FServiceAPI.Resource.getCollectionCollectRules
      >[0] = {
        resourceId: collectionManager.version_resourceID,
      };

      const {
        data: data_CollectionCollectRules,
      }: {
        data: null | {
          status: 0 | 1;
          conditionType: 1 | 2;
          filterConditions: {
            key: 'resourceTitle' | 'resourceTypeCode' | 'authIdentity';
            limitOperatorType:
              | 'INCLUDES'
              | 'NOT_INCLUDES'
              | 'STARTS_WITH'
              | 'ENDS_WITH'
              | 'EQUAL'
              | 'NOT_EQUAL';
            value: string;
          }[];
        };
      } = yield call(FServiceAPI.Resource.getCollectionCollectRules, params9);

      // console.log(data_CollectionCollectRules, 'data_CollectionCollectRules sdifjasldkfjlsdjlfkjslkdjflkjlksjdlk');
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          // pageState: 'loaded',
          version_versions: data_resourceInfo.resourceVersions
            .map((v) => {
              return v.version;
            })
            .reverse(),
          version_resourceInfo: {
            resourceID: data_versionInfo.resourceId,
            resourceName: data_versionInfo.resourceName,
            resourceType: data_versionInfo.resourceType,
            isFinish: data_resourceInfo.serializeStatus === 1,
            isAutomatic: data_CollectionCollectRules?.status === 1 || false,
            // isAutomatic: data_CollectionCollectRules?.status === 1 || false
            // itemsTotalCount: data_versionInfo.catalogueProperty.collection_item_count,
            itemsTotalCount: data_collectionItems.totalItem,
            updateDate: moment(data_versionInfo.updateDate).format(
              'YYYY-MM-DD',
            ),
          },
          version_descriptionText: !!data_draft
            ? data_draft.draftData.descriptionEditorInput
            : data_versionInfo.description,
          // version_rawProperties: result.rawProperties,
          // version_additionalProperties: result.additionalProperties,
          // version_propertyKeysSort: result.keysSort,
          version_systemProperties: result.systemProperties,
          version_customProperties: result.customProperties,
          version_customConfigurations: result.customConfigurations,
          version_directDependencies: !!data_draft
            ? data_draft.draftData.directDependencies
            : data_versionInfo.dependencies.map((r) => {
                return {
                  id: r.resourceId,
                  name: r.resourceName,
                  type: 'resource',
                  versionRange: r.versionRange,
                };
              }),
          version_baseUpcastResources: !!data_draft
            ? data_draft.draftData.baseUpcastResources
            : data_resourceInfo.baseUpcastResources.map((b) => {
                return {
                  resourceID: b.resourceId,
                  resourceName: b.resourceName,
                };
              }),
          version_reload: collectionManager.version_reload + 1,
          version_collectionItemsSetting: !!data_draft
            ? {
                // ...data_draft.draftData.collectionItemsSetting,
                collection_sort_list:
                  data_draft.draftData.collectionItemsSetting
                    ?.collection_sort_list || 'collection_sort_ascending',
                collection_item_title:
                  data_draft.draftData.collectionItemsSetting
                    ?.collection_item_title || 'collection_item_title_rtitle',
                collection_item_no_display:
                  data_draft.draftData.collectionItemsSetting
                    ?.collection_item_no_display ||
                  'collection_item_no_display_show',
                collection_item_image_display:
                  data_draft.draftData.collectionItemsSetting
                    ?.collection_item_image_display ||
                  'collection_item_image_display_show',
                collection_item_descr_display:
                  data_draft.draftData.collectionItemsSetting
                    ?.collection_item_descr_display ||
                  'collection_item_descr_display_show',
                collection_view:
                  data_draft.draftData.collectionItemsSetting
                    ?.collection_view || 'collection_view_list',
              }
            : {
                collection_sort_list:
                  data_versionInfo.catalogueProperty?.collection_sort_list ||
                  'collection_sort_ascending',
                collection_item_title:
                  data_versionInfo.catalogueProperty?.collection_item_title ||
                  'collection_item_title_rtitle',
                collection_item_no_display:
                  data_versionInfo.catalogueProperty
                    ?.collection_item_no_display ||
                  'collection_item_no_display_show',
                collection_item_image_display:
                  data_versionInfo.catalogueProperty
                    ?.collection_item_image_display ||
                  'collection_item_image_display_show',
                collection_item_descr_display:
                  data_versionInfo.catalogueProperty
                    ?.collection_item_descr_display ||
                  'collection_item_descr_display_show',
                collection_view:
                  data_versionInfo.catalogueProperty?.collection_view ||
                  'collection_view_list',
              },
          version_resources_pageSize:
            data_versionInfo.catalogueProperty.collection_view ===
            'collection_view_card'
              ? 6
              : 10,
          version_draftSaveTime: !!data_draft
            ? moment(data_draft.updateDate).format('YYYY-MM-DD HH:mm:ss')
            : '',
          version_collectionItemsChanged:
            (data_draft && data_draft.draftData.collectionItemsChanged) ||
            false,
          version_otherChanged:
            (data_draft && data_draft.draftData.otherChanged) || false,
        },
      });

      yield put<Version_Fetch_CollectionItems_Action>({
        type: 'version_Fetch_CollectionItems',
      });

      // console.log('******************************************************************');
      yield call(FUtil.Tool.promiseSleep, 1000);
      // console.log('##############################################################');

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version_pageState: 'loaded',
        },
      });
    },
    *version_Fetch_CollectionItems(
      {}: Version_Fetch_CollectionItems_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionManager }: ConnectState = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );

      let data_collectionItems: {
        createDate: string;
        itemId: string;
        itemTitle: string;
        sortId: number;
        number: string;
        mountResourceInfo: {
          resourceId: string;
        };
      }[] = [];

      const params: Parameters<
        typeof FServiceAPI.Resource.getCollectionItems_Draft
      >[0] = {
        resourceId: collectionManager.version_resourceID,
        skip:
          (collectionManager.version_resources_pageCurrent - 1) *
          collectionManager.version_resources_pageSize,
        limit: collectionManager.version_resources_pageSize,
        keywords: collectionManager.version_resourceKeywords,
        // sortType: (collectionManager.version_collectionItemsSetting.collection_sorting === 'collection_sort_newest' ||
        //   collectionManager.version_collectionItemsSetting.collection_sorting === 'collection_sort_title_reverse' ||
        //   collectionManager.version_collectionItemsSetting.collection_sorting === 'collection_sort_manual_reverse') ? -1 : 1,
        sortType:
          collectionManager.version_collectionItemsSetting
            ?.collection_sort_list === 'collection_sort_descending'
            ? -1
            : 1,
        isLoadLatestVersionInfo: 1,
      };

      const {
        ret,
        errCode,
        data,
      }: {
        ret: number;
        errCode: number;
        data: {
          skip: number;
          limit: number;
          totalItem: number;
          dataList: {
            createDate: string;
            itemId: string;
            itemTitle: string;
            sortId: number;
            number: string;
            mountResourceInfo: {
              resourceId: string;
            };
          }[];
        };
      } = yield call(FServiceAPI.Resource.getCollectionItems_Draft, params);

      // console.log(data, 'data sdifj;lskdjflk******************************************');

      // console.log(data, 'data sdiofj;sldkjfl;ksdjkljlk');
      data_collectionItems = data.dataList;

      // console.log(data.dataList, collectionManager.version_resources_pageCurrent, '*******************************sdfsdf');
      if (
        data.dataList.length === 0 &&
        collectionManager.version_resources_pageCurrent > 1 &&
        data.totalItem > 0
      ) {
        const version_resources_pageCurrent: number =
          Math.floor(
            (data.totalItem - 1) / collectionManager.version_resources_pageSize,
          ) + 1;
        yield put<ChangeAction>({
          type: 'change',
          payload: {
            version_resources_pageCurrent: version_resources_pageCurrent,
          },
        });

        const parmas: Parameters<
          typeof FServiceAPI.Resource.getCollectionItems_Draft
        >[0] = {
          resourceId: collectionManager.version_resourceID,
          // skip: (collectionManager.version_resources_pageCurrent - 1) * collectionManager.version_resources_pageSize,
          skip:
            (version_resources_pageCurrent - 1) *
            collectionManager.version_resources_pageSize,
          limit: collectionManager.version_resources_pageSize,
          keywords: collectionManager.version_resourceKeywords,
          // sortType: (collectionManager.version_collectionItemsSetting.collection_sorting === 'collection_sort_newest' ||
          //   collectionManager.version_collectionItemsSetting.collection_sorting === 'collection_sort_title_reverse' ||
          //   collectionManager.version_collectionItemsSetting.collection_sorting === 'collection_sort_manual_reverse') ? -1 : 1,
          sortType:
            collectionManager.version_collectionItemsSetting
              ?.collection_sort_list === 'collection_sort_descending'
              ? -1
              : 1,
          isLoadLatestVersionInfo: 1,
        };

        const {
          ret,
          errCode,
          data: data1,
        }: {
          ret: number;
          errCode: number;
          data: {
            skip: number;
            limit: number;
            totalItem: number;
            dataList: {
              createDate: string;
              itemId: string;
              itemTitle: string;
              sortId: number;
              number: string;
              mountResourceInfo: {
                resourceId: string;
              };
            }[];
          };
        } = yield call(FServiceAPI.Resource.getCollectionItems_Draft, parmas);

        data_collectionItems = data1.dataList;
      }

      // console.log(data_collectionItems, 'DDDDDDDDDDDDDDDDDDDDDDDDDDDDD sdi8ojflskdjflkjlk');

      let data_resourceList: any[] = [];
      let auths: {
        itemId: string;
        isAuth: boolean;
      }[] = [];

      if (data_collectionItems.length > 0) {
        const { data: data_resourceList1 } = yield call(
          FServiceAPI.Resource.batchInfo,
          {
            resourceIds: data_collectionItems
              .map((d) => {
                return d.mountResourceInfo.resourceId;
              })
              .join(','),
          },
        );
        data_resourceList = data_resourceList1;

        console.log(
          data_resourceList,
          'data_resourceList sdifj;lsdkjflksdjklfjksdlfjkljlk',
        );

        yield put<ChangeAction>({
          type: 'change',
          payload: {
            version_collectionItems: data_collectionItems.map<
              CollectionManagerState['version_collectionItems'][number]
            >((i, j: number) => {
              // const resAuth = auths.find((dd) => {
              //   return dd.itemId === i.itemId;
              // });

              const resource = data_resourceList.find((r) => {
                return r.resourceId === i.mountResourceInfo.resourceId;
              });
              // console.log(data_collectionItems[j], ' data.dataList[j] sdfsdfdsfsdf');
              return {
                itemID: i.itemId,
                itemTitle: i.itemTitle,
                itemSerial: i.sortId,
                itemNumber: i.number,
                resourceInfo: {
                  id: resource.resourceId,
                  cover:
                    resource.coverImages.length > 0
                      ? resource.coverImages[0]
                      : '',
                  name: resource.resourceName,
                  title: resource.resourceTitle,
                  version: resource.latestVersion,
                  policy: resource.policies
                    .filter((l: any) => {
                      return l.status === 1;
                    })
                    .map((l: any) => l.policyName),
                  type: resource.resourceType,
                  status: resource.status,
                  // authProblem: !!resAuth && !resAuth.isAuth,
                  authProblem: false,
                  updateDate: moment(resource.updateDate).format('YYYY-MM-DD'),
                  username: resource.username,
                  useAvatar: `https://image.freelog.com/avatar/${resource.userId}`,
                  isChoice: resource.operationType === 5,
                  intro: resource.intro,
                  releaseDateTime: moment(
                    resource.resourceVersions[0]?.createDate,
                  ).format('YYYY-MM-DD'),
                },
              };
            }),
            version_resources_totalCount: data.totalItem,
            version_resources_state:
              data.totalItem > 0
                ? 'loaded'
                : collectionManager.version_resourceKeywords === ''
                ? 'noData'
                : 'noSearchResult',
          },
        });

        const parmas1: Parameters<
          typeof FServiceAPI.Resource.getCollectionItemsAuth_Draft
        >[0] = {
          resourceId: collectionManager.version_resourceID,
          itemIds: data_collectionItems
            .map((d) => {
              return d.itemId;
            })
            .join(','),
        };
        const {
          data: data1,
        }: {
          data: {
            itemId: string;
            isAuth: boolean;
          }[];
        } = yield call(
          FServiceAPI.Resource.getCollectionItemsAuth_Draft,
          parmas1,
        );
        // console.log(data1, '********************************* sdoifjsadlkfjlkj');
        auths = data1;
      }

      const list: CollectionManagerState['version_collectionItems'] =
        data_collectionItems.map<
          CollectionManagerState['version_collectionItems'][number]
        >((i, j: number) => {
          const resAuth = auths.find((dd) => {
            return dd.itemId === i.itemId;
          });

          const resource = data_resourceList.find((r) => {
            return r.resourceId === i.mountResourceInfo.resourceId;
          });
          // console.log(data_collectionItems[j], ' data.dataList[j] sdfsdfdsfsdf');
          return {
            itemID: i.itemId,
            itemTitle: i.itemTitle,
            itemSerial: i.sortId,
            itemNumber: i.number,
            resourceInfo: {
              id: resource.resourceId,
              cover:
                resource.coverImages.length > 0 ? resource.coverImages[0] : '',
              name: resource.resourceName,
              title: resource.resourceTitle,
              version: resource.latestVersion,
              policy: resource.policies
                .filter((l: any) => {
                  return l.status === 1;
                })
                .map((l: any) => l.policyName),
              type: resource.resourceType,
              status: resource.status,
              authProblem: !!resAuth && !resAuth.isAuth,
              updateDate: moment(resource.updateDate).format('YYYY-MM-DD'),
              username: resource.username,
              useAvatar: `https://image.freelog.com/avatar/${resource.userId}`,
              isChoice: resource.operationType === 5,
              intro: resource.intro,
              releaseDateTime: moment(
                resource.resourceVersions[0]?.createDate,
              ).format('YYYY-MM-DD'),
            },
          };
        });

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version_collectionItems: list,
          version_resources_totalCount: data.totalItem,
          version_resources_state:
            data.totalItem > 0
              ? 'loaded'
              : collectionManager.version_resourceKeywords === ''
              ? 'noData'
              : 'noSearchResult',
        },
      });
    },
    *version_syncAllProperties(
      {}: Version_SyncAllPropertiesAction,
      { select, call }: EffectsCommandMap,
    ) {
      const { collectionManager }: ConnectState = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );

      const authExcludedItems: {
        resourceId: string;
        excludedType: 'contractId' | 'policyId';
        excludedValue: string;
      }[] = collectionManager.version_authExcludedItems.flatMap((r0) => {
        return r0.contracts.map((r1) => {
          return {
            resourceId: r0.resourceID,
            excludedType: 'contractId' as const,
            excludedValue: r1.contractID,
          };
        });
      });

      const params: Parameters<
        typeof FServiceAPI.Resource.updateCollection
      >[0] = {
        resourceId: collectionManager.version_resourceID,
        authExcludedItems: authExcludedItems,
        customPropertyDescriptors: [
          ...collectionManager.version_customProperties.map((i) => {
            return {
              type: 'readonlyText',
              key: i.key,
              name: i.name,
              remark: i.description,
              defaultValue: i.value,
            };
          }),
          ...collectionManager.version_customConfigurations.map((i) => {
            const isInput: boolean = i.type === 'input';
            const options: string[] = i.select;
            return {
              type: isInput ? 'editableText' : 'select',
              key: i.key,
              name: i.name,
              remark: i.description,
              defaultValue: isInput ? i.input : options[0],
              // defaultValue: isInput ? i.input : '',
              candidateItems: isInput ? undefined : options,
            };
          }),
        ],
      };

      const { data: data_result } = yield call(
        FServiceAPI.Resource.updateCollection,
        params,
      );
    },
    *version_SaveDraft(
      {}: Version_SaveDraft_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionManager }: ConnectState = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version_draftSaving: true,
        },
      });

      const params1: Parameters<typeof FServiceAPI.Resource.lookDraft>[0] = {
        resourceId: collectionManager.version_resourceID,
      };
      const {
        data: data_draft1,
      }: {
        data: null | {
          resourceId: string;
          updateDate: string;
          draftData: IResourceCreateVersionDraftType;
        };
      } = yield call(FServiceAPI.Resource.lookDraft, params1);

      const draftData: ICollectionCreateVersionDraftType = {
        ...(data_draft1?.draftData || {}),
        collectionItemsSetting:
          collectionManager.version_collectionItemsSetting,
        additionalProperties: collectionManager.version_systemProperties
          .filter((sp) => sp.type === 'additional')
          .map((sp) => {
            return {
              key: sp.key,
              value: sp.value,
            };
          }),
        customProperties: collectionManager.version_customProperties,
        customConfigurations: collectionManager.version_customConfigurations,
        directDependencies: collectionManager.version_directDependencies,
        baseUpcastResources: collectionManager.version_baseUpcastResources,
        descriptionEditorInput: collectionManager.version_descriptionText,
        collectionItemsChanged:
          collectionManager.version_collectionItemsChanged,
        otherChanged: collectionManager.version_otherChanged,
      };

      const params: Parameters<
        typeof FServiceAPI.Resource.saveVersionsDraft
      >[0] = {
        resourceId: collectionManager.version_resourceID,
        draftData: draftData,
      };
      const {
        ret,
        errCode,
        data: data_draft,
      }: {
        ret: number;
        errCode: number;
        data: {
          resourceId: string;
          updateDate: string;
          draftData: IResourceCreateVersionDraftType;
        };
      } = yield call(FServiceAPI.Resource.saveVersionsDraft, params);
      if (ret !== 0 || errCode !== 0) {
        fMessage('草稿保存失败', 'error');
        return;
      }
      // if (payload.showSuccessTip) {
      //   fMessage('暂存草稿成功');
      // }
      yield call(FUtil.Tool.promiseSleep, 500);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          version_draftSaving: false,
          version_draftSaveTime: moment(data_draft.updateDate).format(
            'YYYY-MM-DD HH:mm:ss',
          ),
        },
      });
    },
    *version_SaveDate(
      action: Version_SaveDate_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionManager }: ConnectState = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );

      const authExcludedItems: {
        resourceId: string;
        excludedType: 'contractId' | 'policyId';
        excludedValue: string;
      }[] = collectionManager.version_authExcludedItems.flatMap((r0) => {
        return r0.contracts.map((r1) => {
          return {
            resourceId: r0.resourceID,
            excludedType: 'contractId' as const,
            excludedValue: r1.contractID,
          };
        });
      });

      const params: Parameters<
        typeof FServiceAPI.Resource.updateCollection
      >[0] = {
        resourceId: collectionManager.version_resourceID,
        inputAttrs: collectionManager.version_systemProperties
          .filter((sp) => sp.type === 'additional')
          .map((sp) => {
            return {
              key: sp.key,
              value: sp.value,
            };
          }),
        customPropertyDescriptors: [
          ...collectionManager.version_customProperties.map((i) => {
            return {
              type: 'readonlyText',
              key: i.key,
              name: i.name,
              remark: i.description,
              defaultValue: i.value,
            };
          }),
          ...collectionManager.version_customConfigurations.map((i) => {
            const isInput: boolean = i.type === 'input';
            const options: string[] = i.select;
            return {
              type: isInput ? 'editableText' : 'select',
              key: i.key,
              name: i.name,
              remark: i.description,
              defaultValue: isInput ? i.input : options[0],
              // defaultValue: isInput ? i.input : '',
              candidateItems: isInput ? undefined : options,
            };
          }),
        ],
        description: collectionManager.version_descriptionText,
        catalogueProperty: collectionManager.version_collectionItemsSetting,
        // baseUpcastResources: collectionManager.version_baseUpcastResources.map((r) => {
        //   return { resourceId: r.resourceID };
        // }),
        dependencies: collectionManager.version_directDependencies.map((r) => {
          return {
            resourceId: r.id,
            versionRange: r.versionRange || '',
          };
        }),
        // @ts-ignore
        authExcludedItems: authExcludedItems,
        // resolveResources: collectionManager.version_resolveResources,
        isMergeCatalogueDraft: collectionManager.version_collectionItemsChanged
          ? 1
          : 0,
      };

      const {
        ret,
        errCode,
        msg,
        data: data_result,
      } = yield call(FServiceAPI.Resource.updateCollection, params);

      if (ret !== 0 || errCode !== 0) {
        fMessage(msg, 'error');
        return false;
      }

      fMessage('发布成功', 'success');
      return true;

      // yield put<ChangeAction>({
      //   type: 'change',
      //   payload: {
      //     version_collectionItemsChanged: false,
      //     version_otherChanged: false,
      //   },
      // });
    },
    *info_onMount_Page(
      { payload }: Info_OnMount_Page_Action,
      { put, call }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          info_resourceID: payload.resourceID,
          info_pageState: 'loading',
        },
      });

      const params: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: payload.resourceID,
        isLoadPolicyInfo: 1,
        isTranslate: 1,
      };
      const {
        data: data_resourceInfo,
      }: {
        ret: number;
        errCode: number;
        msg: string;
        data: {
          coverImages: string[];
          resourceName: string;
          resourceTitle: string;
          resourceTypeCode: string;
          resourceType: string[];
          intro: string;
          tags: string[];
          serializeStatus: 0 | 1; // 连载状态(0:连载中 1:已完结)
          username: string;
        };
      } = yield call(FServiceAPI.Resource.info, params);
      // yield call(FUtil.Tool.promiseSleep, 1000);

      console.log(
        data_resourceInfo,
        '******************************sdfasdfasdfasdfasdfasdf***',
      );

      const params6: Parameters<
        typeof FServiceAPI.Resource.getCollectionCollectRules
      >[0] = {
        resourceId: payload.resourceID,
      };

      const {
        data: data_CollectionCollectRules,
      }: {
        data: null | {
          status: 0 | 1;
          conditionType: 1 | 2;
          filterConditions: {
            key: 'resourceTitle' | 'resourceTypeCode' | 'authIdentity';
            limitOperatorType:
              | 'INCLUDES'
              | 'NOT_INCLUDES'
              | 'STARTS_WITH'
              | 'ENDS_WITH'
              | 'EQUAL'
              | 'NOT_EQUAL';
            value: string;
          }[];
        };
      } = yield call(FServiceAPI.Resource.getCollectionCollectRules, params6);

      // console.log(data_CollectionCollectRules, 'data_CollectionCollectRules sdf8hsadkjlflsdjlfkjlsdkjflkjsdlkfjlkdsj 9999999999999999999999');

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          info_resourceInfo: {
            coverImages: data_resourceInfo.coverImages,
            resourceName: data_resourceInfo.resourceName,
            resourceTypeCode: data_resourceInfo.resourceTypeCode,
            resourceType: data_resourceInfo.resourceType,
            resourceTitle: data_resourceInfo.resourceTitle,
            intro: data_resourceInfo.intro,
            tags: data_resourceInfo.tags,
          },
          info_pageState: 'loaded',
          info_introduction_EditorText: data_resourceInfo.intro,

          info_username: data_resourceInfo.username,
          info_updateSetting: {
            isFinish: data_resourceInfo.serializeStatus === 1,
            automatic: data_CollectionCollectRules?.status === 1 || false,
            conditionType:
              data_CollectionCollectRules?.conditionType === 1
                ? 'every'
                : 'some',
            conditions:
              data_CollectionCollectRules?.filterConditions.map((fc) => {
                return {
                  ...fc,
                  value: fc.value.includes('/')
                    ? fc.value.split('/')[1]
                    : fc.value,
                  valueError: '',
                };
              }) || [],
          },
        },
      });
    },
    *info_onUnmount_Page(
      {}: Info_OnUnmount_Page_Action,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: initStates,
      });
    },

    *info_onClick_EditTitleBtn(
      {}: Info_OnClick_EditTitleBtn_Action,
      { select, put }: EffectsCommandMap,
    ) {
      const { collectionManager } = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          info_title_IsEditing: true,
          info_title_Input:
            collectionManager.info_resourceInfo.resourceTitle || '',
          info_title_Error: '',
        },
      });
    },
    *info_onClick_CancelEditTitleBtn(
      {}: Info_OnClick_CancelEditTitleBtn_Action,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          info_title_IsEditing: false,
        },
      });
    },
    *info_onClick_SaveEditTitleBtn(
      { payload }: Info_OnClick_SaveTitleBtn_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionManager } = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: collectionManager.info_resourceID,
        resourceTitle: collectionManager.info_title_Input,
      };
      yield call(FServiceAPI.Resource.update, params);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          info_resourceInfo: {
            ...collectionManager.info_resourceInfo,
            resourceTitle: collectionManager.info_title_Input,
          },
          info_title_IsEditing: false,
        },
      });
      // if (payload.trigger === 'collection') {
      yield put<Sider_OnUpdate_Data_Action>({
        type: 'collectionManager/sider_onUpdate_Data',
      });
      // } else {
      //   yield put<OnUpdate_Data_Action>({
      //     type: 'resourceSider/onUpdate_Data',
      //   });
      // }
    },
    *info_onChange_TitleInput(
      { payload }: Info_OnChange_TitleInput_Action,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          info_title_Input: payload.value,
          info_title_Error:
            payload.value === ''
              ? // ? '请输入标题'
                FI18n.i18nNext.t('naming_convention_resource_title_required')
              : payload.value.length > 100
              ? '不超过100个字符'
              : '',
        },
      });
    },
    *info_onClick_SaveIntroductionBtn(
      {}: Info_OnClick_SaveIntroductionBtn_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      // console.log(' *********88888))))');
      const { collectionManager } = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: collectionManager.info_resourceID,
        intro: collectionManager.info_introduction_EditorText,
      };
      yield call(FServiceAPI.Resource.update, params);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          info_resourceInfo: {
            ...collectionManager.info_resourceInfo,
            intro: collectionManager.info_introduction_EditorText.trim(),
          },
          // info_introduction_IsEditing: false,
        },
      });
    },
    *info_onChange_Cover(
      { payload }: Info_OnChange_Cover_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionManager } = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: collectionManager.info_resourceID,
        coverImages: [payload.value],
      };
      yield call(FServiceAPI.Resource.update, params);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          info_resourceInfo: {
            ...collectionManager.info_resourceInfo,
            coverImages: [payload.value],
          },
        },
      });
      // if (payload.trigger === 'collection') {
      yield put<Sider_OnUpdate_Data_Action>({
        type: 'collectionManager/sider_onUpdate_Data',
      });
      // } else {
      //   yield put<OnUpdate_Data_Action>({
      //     type: 'resourceSider/onUpdate_Data',
      //   });
      // }
    },
    *info_onChange_Labels(
      { payload }: Info_OnChange_Labels_Action,
      { select, call, put }: EffectsCommandMap,
    ) {
      const { collectionManager } = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.update>[0] = {
        resourceId: collectionManager.info_resourceID,
        tags: payload.value,
      };
      yield call(FServiceAPI.Resource.update, params);
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          info_resourceInfo: {
            ...collectionManager.info_resourceInfo,
            tags: payload.value,
          },
        },
      });
    },
    *info_Post_UpdateSetting(
      {}: Info_Post_UpdateSetting_Action,
      { put, call, select }: EffectsCommandMap,
    ) {
      const { collectionManager }: ConnectState = yield select(
        ({ collectionManager }: ConnectState) => ({
          collectionManager,
        }),
      );

      const params: Parameters<typeof FServiceAPI.Resource.setCollectRules>[0] =
        {
          resourceId: collectionManager.info_resourceID,
          // @ts-ignore
          serializeStatus: collectionManager.info_updateSetting.isFinish
            ? 1
            : 0,
          status: collectionManager.info_updateSetting.automatic ? 1 : 0,
          conditionType:
            collectionManager.info_updateSetting.conditionType === 'every'
              ? 1
              : 2,
          filterConditions: collectionManager.info_updateSetting.conditions.map(
            (c) => {
              return {
                key: c.key,
                limitOperatorType: c.limitOperatorType,
                value: c.value,
              };
            },
          ),
        };

      yield call(FServiceAPI.Resource.setCollectRules, params);
    },
  },
  reducers: {
    change(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
  subscriptions: {
    setup({}) {},
  },
};

export default Model;
